/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

import { actors } from './transactionActors';

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_QUOTE_AFTER_INQUIRY: 'transition/send-quote-after-inquiry',
  SEND_INIT_QUOTE: 'transition/send-init-quote',
  UPDATE_QUOTE: 'transition/update-quote',
  REQUEST_QUOTE_CHANGE: 'transition/request-quote-change',
  SEND_QUOTE_UPDATE: 'transition/send-quote-update',
  SEND_QUOTE: 'transition/send-quote',
  PROVIDER_DECLINE_QUOTE: 'transition/provider-decline-quote',
  EXPIRE_INIT_QUOTE: 'transition/expire-init-quote',
  CUSTOMER_DECLINE_QUOTE: 'transition/customer-decline-quote',

  EXPIRE_FROM_QUOTE_UPDATED: 'transition/expire-from-quote-updated',
  REOPEN_QUOTE: 'transition/reopen-quote',
  UPDATE_REOPENABLE: 'transition/update-reopenable',
  EXPIRE_FROM_REOPENABLE: 'transition/expire-from-reopenable',
  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.

  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',
  OPERATOR_DECLINE_FROM_INIT_QUOTE: 'transition/operator-decline-from-init-quote',
  OPERATOR_DECLINE_FROM_QUOTE_UPDATED: 'transition/operator-decline-from-quote-updated',
  OPERATOR_DECLINE_FROM_PENDING_PAYMENT: 'transition/operator-decline-from-pending-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  CUSTOMER_CANCEL_PREAUTH: 'transition/customer-cancel-preauth',

  // The operator can accept or decline the offer on behalf of the provider
  FULL_ACCEPT: 'transition/full-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  CUSTOMER_CANCEL_REFUNDABLE: 'transition/customer-cancel-refundable',
  ACCEPTED_REFUNDABLE_PERIOD_OVER: 'transition/accepted-refundable-period-over',

  CUSTOMER_CANCEL: 'transition/customer-cancel',
  CUSTOMER_CANCEL_NO_FEE: 'transition/customer-cancel-no-fee',

  OPERATOR_CANCEL_ACCEPTED: 'transition/operator-cancel-accepted',

  EXPIRE_CANCEL: 'transition/expire-cancel',
  CANCEL_SUCCESS: 'transition/cancel-success',

  EXPIRE_MAIN_PAYMENT: 'transition/expire-main-payment',
  SEND_MAIN_PAYMENT: 'transition/send-main-payment',

  CUSTOMER_CANCEL_PAYMENT_SENT: 'transition/customer-cancel-payment-sent',
  OPERATOR_CANCEL_PAYMENT_SENT: 'transition/operator-cancel-payment-sent',

  PAY_MAIN_PAYMENT: 'transition/pay-main-payment',
  CUSTOMER_CANCEL_PAYMENT_PAID: 'transition/customer-cancel-payment-paid',
  OPERATOR_CANCEL_PAYMENT_PAID: 'transition/operator-cancel-payment-paid',
  PAY_MAIN_PAYMENT_FROM_DISPUTED: 'transition/pay-main-payment-from-disputed',

  EVENT_DONE: 'transition/event-done',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.

  REVIEW_BY_CUSTOMER: 'transition/review-by-customer',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_PROVIDER:
    'transition/update-event-form-from-accepted-by-provider',
  UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_CUSTOMER:
    'transition/update-event-form-from-accepted-by-customer',
  UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_PROVIDER:
    'transition/update-event-form-from-main-payment-sent-by-provider',
  UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_CUSTOMER:
    'transition/update-event-form-from-main-payment-sent-by-customer',
  UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_PROVIDER:
    'transition/update-event-form-from-main-payment-paid-by-provider',
  UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_CUSTOMER:
    'transition/update-event-form-from-main-payment-paid-by-customer',
};

export const transitionActors = {
  // ─────────────────────────────
  // Inquiry & quotes
  // ─────────────────────────────
  [transitions.INQUIRE]: actors.CUSTOMER,
  [transitions.REQUEST_QUOTE_AFTER_INQUIRY]: actors.CUSTOMER,

  [transitions.SEND_INIT_QUOTE]: actors.CUSTOMER,
  [transitions.UPDATE_QUOTE]: actors.PROVIDER,
  [transitions.SEND_QUOTE_UPDATE]: actors.PROVIDER,
  [transitions.SEND_QUOTE]: actors.PROVIDER,
  [transitions.REQUEST_QUOTE_CHANGE]: actors.CUSTOMER,

  [transitions.PROVIDER_DECLINE_QUOTE]: actors.PROVIDER,
  [transitions.CUSTOMER_DECLINE_QUOTE]: actors.CUSTOMER,

  [transitions.EXPIRE_INIT_QUOTE]: actors.SYSTEM,
  [transitions.REQUEST_QUOTE_CHANGE]: actors.CUSTOMER,
  [transitions.EXPIRE_FROM_QUOTE_UPDATED]: actors.SYSTEM,
  [transitions.REOPEN_QUOTE]: actors.PROVIDER,
  [transitions.EXPIRE_FROM_REOPENABLE]: actors.SYSTEM,

  // ─────────────────────────────
  // Payments (preauth)
  // ─────────────────────────────
  [transitions.REQUEST_PAYMENT]: actors.CUSTOMER,
  [transitions.CONFIRM_PAYMENT]: actors.CUSTOMER,

  [transitions.EXPIRE_PAYMENT]: actors.SYSTEM,

  [transitions.ACCEPT]: actors.OPERATOR,
  [transitions.DECLINE]: actors.PROVIDER,
  [transitions.OPERATOR_DECLINE]: actors.OPERATOR,

  [transitions.CUSTOMER_CANCEL_PREAUTH]: actors.CUSTOMER,
  [transitions.EXPIRE]: actors.SYSTEM,

  // ─────────────────────────────
  // Accepted (refundable)
  // ─────────────────────────────
  [transitions.CUSTOMER_CANCEL_REFUNDABLE]: actors.CUSTOMER,
  [transitions.ACCEPTED_REFUNDABLE_PERIOD_OVER]: actors.SYSTEM,

  // ─────────────────────────────
  // Main payment
  // ─────────────────────────────
  [transitions.SEND_MAIN_PAYMENT]: actors.OPERATOR,
  [transitions.PAY_MAIN_PAYMENT]: actors.OPERATOR,
  [transitions.PAY_MAIN_PAYMENT_FROM_DISPUTED]: actors.OPERATOR,

  [transitions.EXPIRE_MAIN_PAYMENT]: actors.OPERATOR,

  [transitions.CUSTOMER_CANCEL_PAYMENT_SENT]: actors.CUSTOMER,
  [transitions.OPERATOR_CANCEL_PAYMENT_SENT]: actors.OPERATOR,

  [transitions.CUSTOMER_CANCEL_PAYMENT_PAID]: actors.CUSTOMER,
  [transitions.OPERATOR_CANCEL_PAYMENT_PAID]: actors.OPERATOR,

  // ─────────────────────────────
  // Full accept
  // ─────────────────────────────
  [transitions.FULL_ACCEPT]: actors.OPERATOR,

  // ─────────────────────────────
  // Cancellation flow
  // ─────────────────────────────
  [transitions.CUSTOMER_CANCEL]: actors.CUSTOMER,
  [transitions.CUSTOMER_CANCEL_NO_FEE]: actors.CUSTOMER,

  [transitions.OPERATOR_CANCEL_ACCEPTED]: actors.OPERATOR,

  [transitions.EXPIRE_CANCEL]: actors.OPERATOR,
  [transitions.CANCEL_SUCCESS]: actors.OPERATOR,

  // ─────────────────────────────
  // Event & completion
  // ─────────────────────────────
  [transitions.EVENT_DONE]: actors.OPERATOR,
  [transitions.COMPLETE]: actors.OPERATOR,

  // ─────────────────────────────
  // Reviews
  // ─────────────────────────────
  [transitions.REVIEW_BY_CUSTOMER]: actors.CUSTOMER,
  [transitions.EXPIRE_REVIEW_PERIOD]: actors.SYSTEM,

  // ─────────────────────────────
  // Event form updates
  // ─────────────────────────────
  [transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_PROVIDER]: actors.PROVIDER,
  [transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_CUSTOMER]: actors.CUSTOMER,

  [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_PROVIDER]: actors.PROVIDER,
  [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_CUSTOMER]: actors.CUSTOMER,

  [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_PROVIDER]: actors.PROVIDER,
  [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_CUSTOMER]: actors.CUSTOMER,
};
/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  INIT_QUOTE_SENT: 'init-quote-sent',
  QUOTE_UPDATED_SENT: 'quote-updated-sent',
  QUOTE_EXPIRED: 'quote-expired',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  ACCEPTED_REFUNDABLE: 'accepted-refundable',
  ACCEPTED_NON_REFUNDABLE: 'accepted-non-refundable',
  EXPIRED_REOPENABLE: 'expired-reopenable',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  MAIN_PAYMENT_SENT: 'main-payment-sent',
  MAIN_PAYMENT_PAID: 'main-payment-paid',
  PENDING_CANCEL: 'pending-cancel',
  DISPUTED: 'disputed',
  DISPUTED_CANCELLED: 'disputed-and-cancelled',
  DISPUTED_CANCEL: 'disputed-cancel',
  OPERATOR_CANCELED: 'operator-canceled',
  EVENT_DATE_PASSED: 'event-date-passed',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.SEND_INIT_QUOTE]: states.INIT_QUOTE_SENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_QUOTE_AFTER_INQUIRY]: states.INIT_QUOTE_SENT,
      },
    },
    [states.INIT_QUOTE_SENT]: {
      on: {
        [transitions.UPDATE_QUOTE]: states.INIT_QUOTE_SENT,
        [transitions.SEND_QUOTE]: states.QUOTE_UPDATED_SENT,
        [transitions.SEND_QUOTE_UPDATE]: states.QUOTE_UPDATED_SENT,
        [transitions.PROVIDER_DECLINE_QUOTE]: states.DECLINED,
        [transitions.EXPIRE_INIT_QUOTE]: states.QUOTE_EXPIRED,
        [transitions.OPERATOR_DECLINE_FROM_INIT_QUOTE]: states.DECLINED,
      },
    },
    [states.QUOTE_UPDATED_SENT]: {
      on: {
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.CUSTOMER_DECLINE_QUOTE]: states.DECLINED,
        [transitions.REQUEST_QUOTE_CHANGE]: states.INIT_QUOTE_SENT,
        [transitions.EXPIRE_FROM_QUOTE_UPDATED]: states.EXPIRED_REOPENABLE,
        [transitions.OPERATOR_DECLINE_FROM_QUOTE_UPDATED]: states.DECLINED,
      },
    },
    [states.EXPIRED_REOPENABLE]: {
      on: {
        [transitions.REOPEN_QUOTE]: states.QUOTE_UPDATED_SENT,
        [transitions.EXPIRE_FROM_REOPENABLE]: states.QUOTE_EXPIRED,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.OPERATOR_DECLINE_FROM_PENDING_PAYMENT]: states.DECLINED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED_REFUNDABLE,
        [transitions.FULL_ACCEPT]: states.MAIN_PAYMENT_PAID,
        [transitions.CUSTOMER_CANCEL_PREAUTH]: states.CANCELED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},

    [states.ACCEPTED_REFUNDABLE]: {
      on: {
        [transitions.CUSTOMER_CANCEL_REFUNDABLE]: states.CANCELED,
        [transitions.ACCEPTED_REFUNDABLE_PERIOD_OVER]: states.ACCEPTED,
      },
    },

    [states.ACCEPTED]: {
      on: {
        [transitions.SEND_MAIN_PAYMENT]: states.MAIN_PAYMENT_SENT,
        [transitions.CUSTOMER_CANCEL]: states.PENDING_CANCEL,
        [transitions.CUSTOMER_CANCEL_NO_FEE]: states.CANCELED,

        [transitions.OPERATOR_CANCEL_ACCEPTED]: states.OPERATOR_CANCELED,

        [transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_PROVIDER]: states.ACCEPTED,
        [transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_CUSTOMER]: states.ACCEPTED,
      },
    },

    [states.MAIN_PAYMENT_SENT]: {
      on: {
        [transitions.PAY_MAIN_PAYMENT]: states.MAIN_PAYMENT_PAID,
        [transitions.EXPIRE_MAIN_PAYMENT]: states.DISPUTED,
        [transitions.CUSTOMER_CANCEL_PAYMENT_SENT]: states.PENDING_CANCEL,

        [transitions.OPERATOR_CANCEL_PAYMENT_SENT]: states.OPERATOR_CANCELED,

        [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_PROVIDER]:
          states.MAIN_PAYMENT_SENT,
        [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_CUSTOMER]:
          states.MAIN_PAYMENT_SENT,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.PAY_MAIN_PAYMENT_FROM_DISPUTED]: states.MAIN_PAYMENT_PAID,
        [transitions.CANCEL_FROM_DISPUTED]: states.DISPUTED_CANCELLED,
      },
    },

    [states.MAIN_PAYMENT_PAID]: {
      on: {
        [transitions.EVENT_DONE]: states.EVENT_DATE_PASSED,
        [transitions.CUSTOMER_CANCEL_PAYMENT_PAID]: states.PENDING_CANCEL,

        [transitions.OPERATOR_CANCEL_PAYMENT_PAID]: states.OPERATOR_CANCELED,

        [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_PROVIDER]:
          states.MAIN_PAYMENT_PAID,
        [transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_CUSTOMER]:
          states.MAIN_PAYMENT_PAID,
      },
    },

    [states.EVENT_DATE_PASSED]: {
      on: {
        [transitions.COMPLETE]: states.DELIVERED,
      },
    },

    [states.PENDING_CANCEL]: {
      on: {
        [transitions.CANCEL_SUCCESS]: states.CANCELED,
        [transitions.EXPIRE_CANCEL]: states.DISPUTED_CANCEL,
      },
    },

    [states.OPERATOR_CANCELED]: {},

    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_BY_CUSTOMER]: states.REVIEWED,
      },
    },

    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.REQUEST_QUOTE_AFTER_INQUIRY,
    transitions.SEND_INIT_QUOTE,
    transitions.UPDATE_QUOTE,
    transitions.SEND_QUOTE_UPDATE,
    transitions.REQUEST_QUOTE_CHANGE,
    transitions.CUSTOMER_DECLINE_QUOTE,
    transitions.EXPIRE_INIT_QUOTE,
    transitions.EXPIRE_FROM_QUOTE_UPDATED,
    transitions.REOPEN_QUOTE,
    transitions.EXPIRE_FROM_REOPENABLE,
    transitions.SEND_QUOTE,
    transitions.EXPIRE_PAYMENT,
    transitions.CUSTOMER_CANCEL_PREAUTH,
    transitions.CUSTOMER_CANCEL_NO_FEE,
    transitions.CUSTOMER_CANCEL_PAYMENT_SENT,
    transitions.CUSTOMER_CANCEL_PAYMENT_PAID,
    transitions.CUSTOMER_CANCEL_REFUNDABLE,
    transitions.CUSTOMER_CANCEL,
    transitions.OPERATOR_CANCEL_ACCEPTED,
    transitions.OPERATOR_CANCEL_PAYMENT_SENT,
    transitions.OPERATOR_CANCEL_PAYMENT_PAID,
    transitions.ACCEPT,
    transitions.FULL_ACCEPT,
    transitions.ACCEPTED_REFUNDABLE_PERIOD_OVER,
    transitions.FULL_ACCEPT,
    transitions.SEND_MAIN_PAYMENT,
    transitions.PAY_MAIN_PAYMENT,
    transitions.COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.EXPIRE_MAIN_PAYMENT,
    transitions.REVIEW_BY_CUSTOMER,
    transitions.PAY_MAIN_PAYMENT_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.OPERATOR_DECLINE_FROM_INIT_QUOTE,
    transitions.OPERATOR_DECLINE_FROM_QUOTE_UPDATED,
    transitions.OPERATOR_DECLINE_FROM_PENDING_PAYMENT,
    transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_CUSTOMER,
    transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_CUSTOMER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_CUSTOMER,
  ].includes(transition);
};

/**
 * Determines whether a transition has a dedicated activity feed message
 * instead of using the default state-based message.
 */
export const hasTransitionSpecificMessage = transition => {
  return [
    transitions.REQUEST_QUOTE_CHANGE,
    transitions.REOPEN_QUOTE,
    transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_CUSTOMER,
    transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_CUSTOMER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_CUSTOMER,
    transitions.FULL_ACCEPT,
  ].includes(transition);
};

export const isTransitionWithCancellationFee = transitionName =>
  [
    transitions.CUSTOMER_CANCEL,
    transitions.CUSTOMER_CANCEL_PAYMENT_SENT,
    transitions.CUSTOMER_CANCEL_PAYMENT_PAID,
  ].includes(transitionName);

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return false;
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_QUOTE_AFTER_INQUIRY,
    transitions.SEND_INIT_QUOTE,
    transitions.SEND_QUOTE_UPDATE,
    transitions.SEND_QUOTE,
    transitions.UPDATE_QUOTE,
    transitions.CONFIRM_PAYMENT,
    transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_CUSTOMER,
    transitions.UPDATE_EVENT_FORM_FROM_ACCEPTED_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_SENT_BY_CUSTOMER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_PROVIDER,
    transitions.UPDATE_EVENT_FORM_FROM_MAIN_PAYMENT_PAID_BY_CUSTOMER,
    transitions.REOPEN_QUOTE,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.REVIEW_BY_CUSTOMER,
    transitions.EXPIRE_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.DECLINE,
    transitions.CUSTOMER_CANCEL_PREAUTH,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
